/*  iceberg-ui  */
import {
    Button,
    Form,
    FormFieldGroup,
    FormInput,
    FormTextArea,
    Link
} from '@auviknetworks/iceberg-ui';
import { Alert, Typography } from '@mui/material';
import { Notify } from 'application/Shared/Helpers';
import { ProviderType } from 'application/Shared/types/tenantProvider';
import { useCreateTenantProviderAsync } from 'hooks/dataAccess/Tenant/Provider/tenantProvider';
import { useCreateTenantProviderAuthAsync } from 'hooks/dataAccess/Tenant/ProviderAuth/tenantProviderAuth';

/*  React  */
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal Components */
import { Modal, ModalBody, ModalFooter } from '../../Modals/Components';

interface OktaModalProps {
    /** The client ID */
    clientId?: string;
    /** Whether the modal is open */
    isOpen: boolean;
    /** Function to close the modal */
    onClose: () => void;
    /** Function to handle form submission */
    onSubmit: (values: OktaFormValues) => Promise<void> | void;
    /** Default values for the form fields */
    defaultValues?: Partial<OktaFormValues>;
    /** Title for the modal */
    title?: string;
}

interface OktaFormValues {
    /** The Okta client domain */
    orgUrl: string;
    /** The Okta client ID */
    clientId: string;
    /** The Okta private key */
    pKey: string;
}

/**
 * Submit button component for the Okta modal that integrates with react-hook-form
 */
const OktaModalSubmitButton = () => {
    const { t } = useTranslation();

    const { formState } = useFormContext();
    const { isSubmitting, isValid } = formState;

    return (
        <Button
            data-trackid="oktaIntegrationSubmit"
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
        >
            {t('Common.connect')}
        </Button>
    );
};

/**
 * Modal component for configuring Okta integration settings
 *
 * @param {OktaModalProps} props - The component props
 * @returns {JSX.Element} The OktaModal component
 */
export const OktaModal = ({
    clientId,
    isOpen,
    onClose,
    onSubmit,
    title
}: OktaModalProps): JSX.Element => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);

    const { createTenantProvider } = useCreateTenantProviderAsync();
    const { createTenantProviderAuth } = useCreateTenantProviderAuthAsync();
    const { clientId: clientIdFromParams } = useParams();

    const platformTenantId = clientId || clientIdFromParams!;

    const upsertTenantProvider = async (
        values: OktaFormValues,
        enabled: boolean
    ) => {
        const providerPayload = {
            enabled,
            identityTenantId: values.clientId,
            platformTenantId,
            providerType: ProviderType.Okta
        };
        return createTenantProvider(providerPayload);
    };

    const upsertTenantProviderAuth = async (values: OktaFormValues) => {
        const authData = {
            clientId: values.clientId,
            orgUrl: values.orgUrl,
            pKey: values.pKey
        };
        const providerAuthPayload = {
            authData,
            platformTenantId,
            providerType: ProviderType.Okta,
            upsert: true
        };

        return createTenantProviderAuth(providerAuthPayload);
    };

    const upsertTenantProviderFn = async (values: OktaFormValues) => {
        setError(null);
        const { error: providerAuthError } = await upsertTenantProviderAuth(
            values
        );
        if (providerAuthError) {
            setError(providerAuthError);
            Notify.e(t('Provider.Shared.error_saving_settings'));
            await upsertTenantProvider(values, false);
            return false;
        }

        const { error: providerError } = await upsertTenantProvider(
            values,
            true
        );
        if (providerError) {
            Notify.e(t('Provider.Shared.error_saving_settings'));
            return false;
        }

        Notify.s(
            t(`Provider.${ProviderType.Okta}Configuration.enabled_and_saved`)
        );
        return true;
    };

    const handleSubmit = async (values: OktaFormValues) => {
        const { orgUrl, clientId, pKey, ...rest } = values;
        const updatedValues = {
            clientId: clientId.trim(),
            orgUrl: orgUrl.trim(),
            pKey: pKey.trim(),
            ...rest
        };

        try {
            const success = await upsertTenantProviderFn(updatedValues);
            if (!success) {
                return;
            }
            await onSubmit(updatedValues);
            onClose();
        } catch (error) {
            Notify.e(t('Provider.Shared.error_saving_settings'));
        }
    };

    return (
        <Modal
            disableEasyClose
            onClose={onClose}
            open={isOpen}
            paperProps={{
                minWidth: '640px',
                width: '100%'
            }}
            title={title || t('Okta.modal.title')}
            titleProps={{ showClose: true }}
        >
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {error && (
                        <Alert
                            icon={false}
                            severity="error"
                            sx={{ mb: '16px' }}
                        >
                            <Typography>{error}</Typography>
                        </Alert>
                    )}
                    <FormFieldGroup>
                        <FormInput
                            name="orgUrl"
                            label={t('Okta.modal.clientDomain')}
                            placeholder={t(
                                'Okta.modal.clientDomainPlaceholder'
                            )}
                            helperText={
                                <Trans
                                    i18nKey="Okta.modal.clientDomainHelper"
                                    components={{
                                        bold: (
                                            <span style={{ fontWeight: 600 }} />
                                        )
                                    }}
                                />
                            }
                            registerProps={{
                                required: t('Form.field.required', {
                                    label: t('Okta.modal.clientDomain')
                                })
                            }}
                        />
                        <FormInput
                            name="clientId"
                            label={t('Okta.modal.clientId')}
                            placeholder={t('Okta.modal.clientIdPlaceholder')}
                            helperText={
                                <Trans
                                    i18nKey="Okta.modal.clientIdHelper"
                                    components={{
                                        OktaLink: (
                                            <Link
                                                href="https://support.auvik.com/hc/en-us/articles/40298033902612-Okta-Identity-Management-Integration"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            />
                                        )
                                    }}
                                />
                            }
                            registerProps={{
                                required: t('Form.field.required', {
                                    label: t('Okta.modal.clientId')
                                })
                            }}
                        />
                        <FormTextArea
                            name="pKey"
                            label={t('Okta.modal.privateKey')}
                            placeholder={t('Okta.modal.privateKeyPlaceholder')}
                            helperText={
                                <Trans
                                    i18nKey="Okta.modal.privateKeyHelper"
                                    components={{
                                        OktaLink: (
                                            <Link
                                                href="https://support.auvik.com/hc/en-us/articles/40298033902612-Okta-Identity-Management-Integration"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            />
                                        )
                                    }}
                                />
                            }
                            registerProps={{
                                required: t('Form.field.required', {
                                    label: t('Okta.modal.privateKey')
                                })
                            }}
                            rows={8}
                        />
                    </FormFieldGroup>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="secondary" type="button">
                        {t('Common.cancel')}
                    </Button>
                    <OktaModalSubmitButton />
                </ModalFooter>
            </Form>
        </Modal>
    );
};
